import { useRestaurant } from 'restaurantAdmin/context/useRestaurant';
import { Button, ButtonVariants } from '@components/button/Button';
import cx from 'classnames';
import { SimpleBar } from '@shared/components/simpleBar/simpleBar';
import type { RestaurantEvent } from '@shared/types/restaurants';
import { useHtmlId } from '@shared/hooks/useHtmlId';
import { useCallback, useMemo, useState } from 'react';
import type {
  Control,
  FieldPath,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form';
import { useFieldArray } from 'react-hook-form';
import { AddonCard } from './AddonCard';
import { AddonForm } from './AddonForm';
import addonsStyles from './AddOnsStep.scss';
import { buildAddonTierOptions } from './TierSelectionPopover';
import {
  DEFAULT_EMPTY_ADD_ON,
  EVENT_STEPS,
  type EventAddOnFormItem,
  type EventFormData,
  isBlankAddOnRow,
  isPersistableAddOnFormItem,
} from '../RestaurantEventForm';

export type AddOnsStepProps = {
  control: Control<EventFormData>;
  setValue: UseFormSetValue<EventFormData>;
  watch: UseFormWatch<EventFormData>;
  restaurantEvent?: RestaurantEvent | null;
};

export const AddOnsStep = ({
  control,
  setValue,
  watch,
  restaurantEvent,
}: AddOnsStepProps) => {
  const restaurant = useRestaurant();
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'addOns',
  });

  const addOnRemoveTooltipBase = useHtmlId('event-add-on-remove');

  const [showAllAsCards, setShowAllAsCards] = useState(() =>
    Boolean(restaurantEvent?.addOns?.length),
  );
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [cardEditIndex, setCardEditIndex] = useState<number | null>(null);

  const ticketTiers = watch('ticketTiers') ?? [];
  const addOnsValues = watch('addOns') ?? [];

  const canRemoveAddOnAtIndex = (index: number) => {
    const row = addOnsValues[index];
    if (fields.length === 1 && isBlankAddOnRow(row)) {
      return false;
    }
    return (
      fields.length > 1 ||
      Boolean(row?.id) ||
      isBlankAddOnRow(row) ||
      (row != null && isPersistableAddOnFormItem(row))
    );
  };

  const tierOptions = useMemo(
    () => buildAddonTierOptions(ticketTiers, restaurantEvent?.tiers),
    [ticketTiers, restaurantEvent?.tiers],
  );

  const resolveTierNames = useCallback(
    (tierIds: string[]) =>
      tierIds
        .map(
          (tierId) => tierOptions.find((option) => option.id === tierId)?.name,
        )
        .filter((name): name is string => Boolean(name)),
    [tierOptions],
  );

  const addOnsStepLabel =
    EVENT_STEPS.find((step) => step.id === 'event-add-ons')?.label ??
    'Event Add-Ons';

  const rowHasCardContent = (item: EventAddOnFormItem | undefined) => {
    if (!item) return false;
    if (item.id) return true;
    return Boolean(
      item.imageKey?.trim() &&
        item.name?.trim() &&
        String(item.price ?? '').trim() !== '' &&
        Number.isFinite(Number.parseFloat(String(item.price))),
    );
  };

  const showCardForIndex = (index: number) => {
    const item = addOnsValues[index];
    if (!rowHasCardContent(item)) return false;
    if (editingIndex === index) return false;
    return showAllAsCards;
  };

  const handleAddAddOn = () => {
    if (!showAllAsCards && fields.length > 0) {
      setShowAllAsCards(true);
      setCardEditIndex(null);
    }

    const nextIndex = fields.length;
    append({ ...DEFAULT_EMPTY_ADD_ON });
    setEditingIndex(nextIndex);
    setCardEditIndex(null);
  };

  const exitCardEditMode = useCallback(() => {
    setEditingIndex(null);
    setCardEditIndex(null);
  }, []);

  const exitNewAddOnToCard = useCallback(() => {
    setEditingIndex(null);
    setCardEditIndex(null);
    setShowAllAsCards(true);
  }, []);

  const handleRemoveAddOnAtIndex = useCallback(
    (index: number) => {
      const isLastField = fields.length <= 1;
      remove(index);
      if (editingIndex === index) setEditingIndex(null);
      if (cardEditIndex === index) setCardEditIndex(null);
      if (isLastField) {
        setShowAllAsCards(false);
        append({ ...DEFAULT_EMPTY_ADD_ON });
      }
    },
    [append, cardEditIndex, editingIndex, fields.length, remove],
  );

  const addOnCardData = (index: number) => {
    const item = addOnsValues[index];
    return {
      id: item?.id,
      name: item?.name ?? '',
      description: item?.description || null,
      price: parseFloat(item?.price ?? '') || null,
      imageKey: item?.imageKey || null,
      tierNames: resolveTierNames(item?.tierIds ?? []),
    };
  };

  return (
    <section className={addonsStyles.eventFormStepAddOns}>
      <header>
        <h2 className={addonsStyles.formTitle}>{addOnsStepLabel}</h2>
        <Button
          label="Add add-on"
          variant={ButtonVariants.Secondary}
          onClick={handleAddAddOn}
          iconName="plus"
        />
      </header>
      <p
        className={cx(
          addonsStyles.stepSubtitle,
          addonsStyles.addonsStepSubtitleFlat,
        )}
      >
        Optional upgrades and extras for ticket holders
      </p>

      <SimpleBar className={addonsStyles.addonsListScroll}>
        <div className={addonsStyles.addonsList}>
          {fields.map((field, index) =>
            showCardForIndex(index) ? (
              <div key={field.id} className={addonsStyles.addonRowCardWrap}>
                <AddonCard
                  addon={addOnCardData(index)}
                  onEdit={() => {
                    setEditingIndex(index);
                    setShowAllAsCards(true);
                    setCardEditIndex(index);
                  }}
                  onDelete={
                    canRemoveAddOnAtIndex(index)
                      ? () => handleRemoveAddOnAtIndex(index)
                      : undefined
                  }
                />
              </div>
            ) : (
              <div key={field.id} className={addonsStyles.addonRow}>
                <div className={addonsStyles.addonRowForm}>
                  <AddonForm
                    control={control}
                    setValue={setValue}
                    index={index}
                    restaurantId={restaurant.id}
                    ticketTiers={ticketTiers}
                    savedEventTiers={restaurantEvent?.tiers}
                    onDoneCardEdit={
                      cardEditIndex === index ? exitCardEditMode : undefined
                    }
                    onDoneNewAddOn={
                      !addOnsValues[index]?.id ? exitNewAddOnToCard : undefined
                    }
                    onClearRow={
                      fields.length === 1 && !addOnsValues[index]?.id
                        ? () => {
                            setValue(
                              `addOns.${index}` as FieldPath<EventFormData>,
                              { ...DEFAULT_EMPTY_ADD_ON },
                              { shouldDirty: true },
                            );
                          }
                        : undefined
                    }
                    onRemove={
                      (fields.length > 1 || Boolean(addOnsValues[index]?.id)) &&
                      canRemoveAddOnAtIndex(index)
                        ? () => handleRemoveAddOnAtIndex(index)
                        : undefined
                    }
                    removeTooltipId={
                      (fields.length > 1 || Boolean(addOnsValues[index]?.id)) &&
                      canRemoveAddOnAtIndex(index)
                        ? `${addOnRemoveTooltipBase}-${field.id}`
                        : undefined
                    }
                  />
                </div>
              </div>
            ),
          )}
        </div>
      </SimpleBar>
    </section>
  );
};
